import { IToIBill } from 'api/newDLTransferAPI'
import {
  I2IInsuranceStatus,
  IToIContractType,
  IToIRequestFromBE,
  IToIRequestInsuranceFromBE,
  Nationality,
  ObjectWithTranslation,
} from 'common/types/transformedRespTypes'
import { BillStatus, IToIRequestsStatus } from 'common/types/commonTypes'

import { formatObjectWithTranslationFromBE } from '../objectWithTranslation'

export type IToIRequestInsuranceInfo = {
  label: string
  labelEn: string
  policyPremium: number
  tameeniAmount: number
  insuranceSource: string
  policyNumber: number | null
  insuranceStatus: I2IInsuranceStatus
  issuanceDate: string | null
  activationDate: string | null
  expirationDate: string | null
}

export type IToIRequest = {
  id: number
  createdAt: string
  source?: 'EMPLOYER' | 'HRSD' | 'NEW_EMPLOYER' | 'MARKETPLACE'
  bill: {
    operationalFees: number
    servicePrice: number
    serviceTotal: number
    contractInsuranceFee: number
    healthInsuranceFee: number
    vat: number
  }
  currentEmployer: {
    id: number
    idNumber: number
    date: string
    mobileNumber: string
    name: string
  }
  newEmployer: {
    id: number
    idNumber: number
    date: string
    mobileNumber: string
    name: string
  }
  laborer: {
    iqama: number
    name: string
    mobile: string
    occupation: ObjectWithTranslation
    nationality?: Nationality
  }
  laborerSalary: number
  status: IToIRequestsStatus | string
  contracts: Array<{
    id: number
    legalText: string
    legalTextEn: string
    startedAt: string | null
    type: IToIContractType
  }>
  insurance: {
    health?: IToIRequestInsuranceInfo
    contract?: IToIRequestInsuranceInfo
  }
  isFinanciallyCapable: boolean
  shouldViewZatcaBill?: boolean
  contractDuration: number
  financialDetail: {
    minSalary: number
    minBankBalance: number
  }
  rejectionReasons: Array<ObjectWithTranslation>
}

export const getEmptyLaborerIToIRequest = (): IToIRequest => ({
  id: 0,
  createdAt: '',
  bill: {
    operationalFees: 0,
    servicePrice: 0,
    serviceTotal: 0,
    contractInsuranceFee: 0,
    healthInsuranceFee: 0,
    vat: 0,
  },
  currentEmployer: {
    id: 0,
    idNumber: 0,
    date: '',
    mobileNumber: '',
    name: '',
  },
  newEmployer: {
    id: 0,
    idNumber: 0,
    date: '',
    mobileNumber: '',
    name: '',
  },
  laborer: {
    iqama: 0,
    name: '',
    mobile: '',
    occupation: {
      id: 0,
      label: '',
      labelEn: '',
    },
    nationality: {
      id: 0,
      label: '',
      labelEn: '',
      isOnlineContractingAllowed: false,
      code: '',
      abbreviation: '',
    },
  },
  laborerSalary: 0,
  status: '',
  contracts: [],
  insurance: {},
  isFinanciallyCapable: false,
  contractDuration: 0,
  financialDetail: {
    minSalary: 0,
    minBankBalance: 0,
  },
  rejectionReasons: [],
  source: undefined,
})

const convertInsurance = (insurance?: IToIRequestInsuranceFromBE): IToIRequestInsuranceInfo | undefined => {
  return insurance
    ? {
        label: insurance.insurance_company_name_ar,
        labelEn: insurance.insurance_company_name_en,
        policyPremium: insurance.policy_premium,
        tameeniAmount: insurance.tameeni_amount,
        insuranceSource: insurance.insurance_source,
        policyNumber: insurance.policy_number,
        insuranceStatus: insurance.status,
        issuanceDate: insurance.policy_issue_date,
        expirationDate: insurance.policy_expiry_date,
        activationDate: insurance.policy_activation_date,
      }
    : undefined
}

export const convertIToIRequestFromResponse = (request: IToIRequestFromBE): IToIRequest => ({
  id: request.id,
  createdAt: request.created_at,
  source: request.source,
  bill: {
    operationalFees: request.bill?.breakdown.operational_fees_without_vat || 0,
    servicePrice: request.bill?.breakdown.service_price || 0,
    serviceTotal: request.bill?.breakdown.service_total || 0,
    contractInsuranceFee: request.bill?.breakdown.contract_insurance_fee || 0,
    healthInsuranceFee: request.bill?.breakdown.health_insurance_fee || 0,
    vat: request.bill?.breakdown.vat || 0,
  },
  currentEmployer: {
    id: request.current_employer.id,
    idNumber: request.current_employer.id_number,
    date: request.current_employer.date,
    mobileNumber: request.current_employer.mobile_number,
    name: request.current_employer.name || '-',
  },
  newEmployer: {
    id: request.new_employer.id,
    idNumber: request.new_employer.id_number,
    date: request.new_employer.date,
    mobileNumber: request.new_employer.mobile_number,
    name: request.new_employer.name || '-',
  },
  laborer: {
    iqama: request.laborer.id_number,
    name: request.laborer.name,
    mobile: request.laborer.mobile_number,
    occupation: {
      id: request.laborer.occupation.id || 0,
      label: request.laborer.occupation.label,
      labelEn: request.laborer.occupation.label_en,
    },
    nationality: {
      id: request.laborer.nationality?.id || 0,
      label: request.laborer.nationality?.label || '',
      labelEn: request.laborer.nationality?.label_en || '',
      abbreviation: request.laborer.nationality?.abbreviation || '',
      isOnlineContractingAllowed: !!request.laborer.nationality?.is_online_contracting_allowed || false,
      code: request.laborer.nationality?.code || '',
    },
  },
  laborerSalary: request.laborer_salary,
  status: request.status,
  contracts: request.contracts
    ? request.contracts.map((contract) => ({
        id: contract.id,
        legalText: contract.legal_text,
        legalTextEn: contract.legal_text_en,
        startedAt: contract.started_at,
        type: contract.type,
      }))
    : [],
  insurance: {
    health: convertInsurance(request.insurance?.find((insurance) => insurance.insurance_type === 'HEALTH')),
    contract: convertInsurance(request.insurance?.find((insurance) => insurance.insurance_type === 'CONTRACT')),
  },
  isFinanciallyCapable: request.is_financially_capable,
  shouldViewZatcaBill: request.should_view_zatca_bill,
  contractDuration: request.contract_duration,
  financialDetail: {
    minSalary: request.financial_details ? request.financial_details.min_salary : 0,
    minBankBalance: request.financial_details ? request.financial_details.min_bank_balance : 0,
  },
  rejectionReasons: request.rejection_reasons?.map(formatObjectWithTranslationFromBE) || [],
})

export type Bill = {
  billNumber: number
  billIssueDate: string
  contractRequestNumber: number
  status: BillStatus
  paidAt: string
  amount: number
  totalAmount: number
  currentEmployer: {
    name: string
    idNumber: number
  }
  newEmployer: {
    name: string
    idNumber: number
  }
  breakdown: {
    servicePrice: number
    bankFees: number
    operationalFees: number
    vat: number
  }
}
export const getEmptyLaborerIToIBill = (): Bill => ({
  billNumber: 0,
  billIssueDate: '',
  contractRequestNumber: 0,
  status: 0,
  paidAt: '',
  amount: 0,
  totalAmount: 0,
  currentEmployer: {
    name: '',
    idNumber: 0,
  },
  newEmployer: {
    name: '',
    idNumber: 0,
  },
  breakdown: {
    servicePrice: 0,
    bankFees: 0,
    operationalFees: 0,
    vat: 0,
  },
})
export const convertIToIBillFromResponse = (bill: IToIBill): Bill => ({
  billNumber: bill.bill_number,
  billIssueDate: bill.bill_issue_date,
  contractRequestNumber: bill.contract_request_number,
  status: bill.status,
  paidAt: bill.paid_at,
  amount: bill.amount,
  totalAmount: bill.total_amount,
  currentEmployer: {
    name: bill.current_employer.name,
    idNumber: bill.current_employer.id_number,
  },
  newEmployer: {
    name: bill.new_employer.name,
    idNumber: bill.new_employer.id_number,
  },
  breakdown: {
    servicePrice: bill.breakdown.service_price,
    bankFees: bill.breakdown.bank_fees_without_vat,
    operationalFees: bill.breakdown.operational_fees_without_vat,
    vat: bill.breakdown.vat,
  },
})
